import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FadeUp } from "./Reveal";
import { useAuth } from "@/context/AuthContext";
import { ForgotForm } from "@/pages/Gallery";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F4F0] placeholder:text-[#525252] focus:outline-none focus:border-[#C9A26B] transition-colors duration-300 text-sm";

const AccountPanel = () => {
  const { user, logout } = useAuth();
  const [weddings, setWeddings] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/my/weddings").then((r) => setWeddings(r.data)).catch(() => setWeddings([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/weddings/join", { code: code.trim().toUpperCase() });
      toast.success("Wedding added to your account");
      setCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-10" data-testid="account-panel">
      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-2">Signed in as</p>
        <p className="font-serif text-3xl text-[#F5F4F0]" data-testid="account-name">{user.name || "Friend"}</p>
        <p className="text-sm text-[#A3A3A3] mt-1" data-testid="account-email">{user.email}</p>
      </div>

      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-4">Your weddings</p>
        {weddings === null ? (
          <p className="text-[#525252] text-sm">Loading…</p>
        ) : weddings.length === 0 ? (
          <p className="text-sm text-[#A3A3A3]" data-testid="account-no-weddings">No weddings linked yet — join one with a code below.</p>
        ) : (
          <div className="flex flex-col divide-y divide-white/10 border-y border-white/10" data-testid="account-weddings">
            {weddings.map((w) => (
              <a key={w.code} href={`/gallery?code=${w.code}`} data-testid={`account-wedding-${w.code}`}
                className="flex items-center justify-between py-4 group">
                <div>
                  <p className="font-serif text-xl text-[#F5F4F0] group-hover:text-[#C9A26B] transition-colors duration-300">{w.couple_name}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#737373] mt-1">{w.role === "couple" ? "Your wedding" : "Guest"} · {w.photo_count} photos</p>
                </div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A26B]">Open →</span>
              </a>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={join} className="flex flex-col gap-6" data-testid="account-join-form">
        <div>
          <label htmlFor="account-code" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Add another wedding — gallery code</label>
          <input id="account-code" required data-testid="account-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="E.g. ELENA-4821" className={`${inputCls} tracking-[0.2em] uppercase`} />
        </div>
        <button type="submit" disabled={busy} data-testid="account-join-btn"
          className="self-start text-xs tracking-[0.3em] uppercase border border-[#C9A26B]/60 px-8 py-4 text-[#C9A26B] hover:bg-[#C9A26B] hover:text-[#0A0A0A] transition-colors duration-400 disabled:opacity-50">
          {busy ? "Adding…" : "Add Wedding"}
        </button>
      </form>

      <button
        onClick={async () => { await logout(); toast.success("Signed out — see you soon"); }}
        data-testid="account-logout-btn"
        className="self-start text-xs tracking-[0.35em] uppercase text-[#737373] hover:text-[#F5F4F0] border border-white/20 px-8 py-4 transition-colors duration-300">
        Log Out
      </button>
    </div>
  );
};

export const Registration = () => {
  const { register, login, user } = useAuth();
  const [mode, setMode] = useState("register");
  const [forgot, setForgot] = useState(false);
  const [directKey, setDirectKey] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", type: "couple" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const res =
      mode === "register"
        ? await register(form.name, form.email, form.password, form.type)
        : await login(form.email, form.password);
    setSending(false);
    if (res.ok) {
      toast.success(mode === "register" ? "Your account is ready." : "Welcome back.", {
        description: "Opening your client area…",
      });
      setTimeout(() => window.location.assign("/gallery"), 1200);
    } else if (mode === "register" && String(res.error).includes("already registered")) {
      setMode("login");
      toast.error("This email already has an account", {
        description: "Just enter your password below to sign in — your wedding is waiting.",
      });
    } else {
      toast.error(mode === "register" ? "Could not register you" : "Could not sign you in", { description: res.error });
    }
  };

  return (
    <section id="register" className="px-6 md:px-12 py-28 md:py-40 bg-[#121212] border-t border-white/5" data-testid="register-section">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-8">
        <FadeUp className="md:col-span-6">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">{user ? "Your Account" : "Registration"}</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#F5F4F0] leading-[1.05] mb-8">
            {user ? <>Welcome <span className="font-accent italic text-[#C9A26B]">back</span></> : <>Get your <span className="font-accent italic text-[#C9A26B]">Photos</span></>}
          </h2>
          {user ? (
            <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed max-w-md mb-10">
              Your weddings, your photos, your favourites — everything lives here.
              Open a gallery, or add another wedding with a code.
            </p>
          ) : (
            <>
              <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed max-w-md mb-10">
                Couples planning their day, and guests who'll be part of it — register here
                and we'll keep you close to everything that matters.
              </p>
              <div className="flex flex-col gap-4 text-xs tracking-[0.25em] uppercase text-[#737373]">
                <p><span className="text-[#C9A26B]">—</span>&nbsp;&nbsp;Couples: your day, beautifully kept</p>
                <p><span className="text-[#C9A26B]">—</span>&nbsp;&nbsp;Guests: find yourself in the story</p>
              </div>
            </>
          )}
        </FadeUp>

        <FadeUp delay={0.15} className="md:col-span-5 md:col-start-8">
          {user === null ? (
            <p className="text-[#525252]" data-testid="auth-checking">Loading…</p>
          ) : user ? (
            <AccountPanel />
          ) : (
          <>
          <div className="border-b border-white/10 mb-14 pb-10" data-testid="direct-entry-block">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-2">Just here for the photos?</p>
            <p className="text-xs text-[#737373] mb-6">No account, no email — a direct access key opens the gallery instantly.</p>
            <form onSubmit={(e) => { e.preventDefault(); if (directKey.trim()) window.location.assign(`/gallery?code=${encodeURIComponent(directKey.trim().toUpperCase())}`); }}
              className="flex flex-col gap-6" data-testid="direct-entry-form">
              <input value={directKey} onChange={(e) => setDirectKey(e.target.value)} data-testid="direct-key-input"
                placeholder="E.g. ELENA-9F2A4C7E1B3D5A6F" className={`${inputCls} tracking-[0.15em] uppercase`} />
              <button type="submit" data-testid="direct-entry-btn"
                className="self-start text-xs tracking-[0.3em] uppercase border border-white/25 px-10 py-4 text-[#F5F4F0] hover:border-[#C9A26B] hover:text-[#C9A26B] transition-colors duration-400">
                Enter Gallery
              </button>
            </form>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-10" data-testid="register-form">
            <div className="flex gap-4" data-testid="auth-mode-toggle">
              {[["register", "Register"], ["login", "Sign In"]].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  data-testid={`auth-mode-${val}`}
                  onClick={() => setMode(val)}
                  className={`flex-1 text-xs tracking-[0.25em] uppercase py-4 border transition-colors duration-400 ${
                    mode === val
                      ? "bg-[#C9A26B] text-[#0A0A0A] border-[#C9A26B]"
                      : "border-white/20 text-[#A3A3A3] hover:border-[#C9A26B]/50 hover:text-[#F5F4F0]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {mode === "register" && (
              <div className="flex gap-4" data-testid="register-type-toggle">
                {[["couple", "I'm a couple"], ["guest", "I'm a guest"]].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`register-type-${val}`}
                    onClick={() => setForm((f) => ({ ...f, type: val }))}
                    className={`flex-1 text-xs tracking-[0.25em] uppercase py-4 border transition-colors duration-400 ${
                      form.type === val
                        ? "bg-[#C9A26B] text-[#0A0A0A] border-[#C9A26B]"
                        : "border-white/20 text-[#A3A3A3] hover:border-[#C9A26B]/50 hover:text-[#F5F4F0]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {mode === "register" && (
              <div>
                <label htmlFor="reg-name" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Your name *</label>
                <input id="reg-name" required={mode === "register"} data-testid="register-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Alex & Sam" className={inputCls} />
              </div>
            )}
            <div>
              <label htmlFor="reg-email" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Email *</label>
              <input id="reg-email" type="email" required data-testid="register-email-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@love.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="reg-password" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Password *</label>
              <input id="reg-password" type="password" required minLength={6} data-testid="register-password-input" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min. 6 characters" className={inputCls} />
            </div>
            <button type="submit" disabled={sending} data-testid="register-submit-btn"
              className="self-start text-xs tracking-[0.35em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-12 py-5 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50">
              {sending ? "One moment…" : mode === "register" ? "Register" : "Sign In"}
            </button>
            {mode === "login" && !forgot && (
              <button type="button" onClick={() => setForgot(true)} data-testid="home-forgot-open"
                className="self-start text-xs tracking-[0.2em] uppercase text-[#737373] hover:text-[#C9A26B] transition-colors duration-300">
                Forgot your password?
              </button>
            )}
          </form>
          {mode === "login" && forgot && (
            <div className="border border-white/10 p-8 flex flex-col gap-6 mt-4" data-testid="home-forgot-block">
              <ForgotForm />
              <button type="button" onClick={() => setForgot(false)} data-testid="home-forgot-back"
                className="self-start text-xs tracking-[0.2em] uppercase text-[#737373] hover:text-[#C9A26B] transition-colors duration-300">
                Back to sign in
              </button>
            </div>
          )}
          </>
          )}
        </FadeUp>
      </div>
    </section>
  );
};
